import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import AdminLayout from '../../../components/admin/AdminLayout';

const AddClientPage = () => {
    const navigate = useNavigate();

    // Form State
    const [formData, setFormData] = useState({
        // Personal Information
        first_name: '',
        middle_name: '',
        last_name: '',
        dob: '',
        gender: '',
        nationality: '',
        marital_status: '',
        passport_number: '',

        // Contact Information
        primary_email: '',
        secondary_email: '',
        primary_phone: '',
        secondary_phone: '',

        // Address
        street_address: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: '',

        // Preferences
        communication_preference: 'email',
        marketing_consent: false,
        data_processing_consent: false,
        tags: '',
        notes: ''
    });

    // Education entries
    const [education, setEducation] = useState([{
        level: '',
        field_of_study: '',
        institution: '',
        graduation_year: ''
    }]);

    // Work experience entries
    const [experience, setExperience] = useState([{
        job_title: '',
        company: '',
        years: '',
        industry: ''
    }]);

    // Language entries
    const [languages, setLanguages] = useState([{
        language: '',
        proficiency: '',
        test_type: '',
        test_score: ''
    }]);

    // Financial info
    const [financial, setFinancial] = useState({
        annual_income: '',
        source_of_funds: '',
        financial_notes: ''
    });

    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [expandedSections, setExpandedSections] = useState({
        personal: true,
        contact: true,
        address: false,
        education: false,
        experience: false,
        languages: false,
        financial: false,
        preferences: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleEducationChange = (index, field, value) => {
        const updated = [...education];
        updated[index][field] = value;
        setEducation(updated);
    };

    const addEducation = () => {
        setEducation([...education, { level: '', field_of_study: '', institution: '', graduation_year: '' }]);
    };

    const removeEducation = (index) => {
        if (education.length > 1) {
            setEducation(education.filter((_, i) => i !== index));
        }
    };

    const handleExperienceChange = (index, field, value) => {
        const updated = [...experience];
        updated[index][field] = value;
        setExperience(updated);
    };

    const addExperience = () => {
        setExperience([...experience, { job_title: '', company: '', years: '', industry: '' }]);
    };

    const removeExperience = (index) => {
        if (experience.length > 1) {
            setExperience(experience.filter((_, i) => i !== index));
        }
    };

    const handleLanguageChange = (index, field, value) => {
        const updated = [...languages];
        updated[index][field] = value;
        setLanguages(updated);
    };

    const addLanguage = () => {
        setLanguages([...languages, { language: '', proficiency: '', test_type: '', test_score: '' }]);
    };

    const removeLanguage = (index) => {
        if (languages.length > 1) {
            setLanguages(languages.filter((_, i) => i !== index));
        }
    };

    const handleFinancialChange = (e) => {
        const { name, value } = e.target;
        setFinancial(prev => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};

        // Required fields
        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.primary_email.trim()) {
            newErrors.primary_email = 'Primary email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primary_email)) {
            newErrors.primary_email = 'Invalid email format';
        }
        if (!formData.primary_phone.trim()) newErrors.primary_phone = 'Primary phone is required';

        // Optional email validation
        if (formData.secondary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondary_email)) {
            newErrors.secondary_email = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            setError('Please fix the errors in the form');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Prepare client data
            const clientData = {
                first_name: formData.first_name.trim(),
                middle_name: formData.middle_name.trim() || null,
                last_name: formData.last_name.trim(),
                dob: formData.dob || null,
                gender: formData.gender || null,
                nationality: formData.nationality || null,
                marital_status: formData.marital_status || null,
                passport_no: formData.passport_number.trim() || null,
                primary_email: formData.primary_email.trim(),
                secondary_email: formData.secondary_email.trim() || null,
                primary_phone: formData.primary_phone.trim(),
                secondary_phone: formData.secondary_phone.trim() || null,
                tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
                notes: formData.notes.trim() || null,
                education_data: education.filter(e => e.level || e.institution).length > 0 ? education : [],
                experience_data: experience.filter(e => e.job_title || e.company).length > 0 ? experience : [],
                language_data: languages.filter(l => l.language).length > 0 ? languages : [],
                financial_data: (financial.annual_income || financial.source_of_funds) ? financial : {}
            };

            // Insert client
            const { data: newClient, error: clientError } = await supabase
                .from('clients')
                .insert([clientData])
                .select()
                .single();

            if (clientError) throw clientError;

            // Insert address if provided
            if (formData.street_address || formData.city || formData.country) {
                const addressData = {
                    client_id: newClient.id,
                    type: 'primary',
                    line1: formData.street_address.trim() || null,
                    line2: null,
                    city: formData.city.trim() || null,
                    state: formData.state_province.trim() || null,
                    postal_code: formData.postal_code.trim() || null,
                    country: formData.country.trim() || null
                };

                const { error: addressError } = await supabase
                    .from('client_addresses')
                    .insert([addressData]);

                if (addressError) console.error('Address insert error:', addressError);
            }

            // Navigate to the new client's detail page
            navigate(`/admin/clients/${newClient.id}`);
        } catch (err) {
            console.error('Error creating client:', err);
            setError(err.message || 'Failed to create client');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/clients');
    };

    return (
        <AdminLayout>
            <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
                {/* Header */}
                <div className="mb-8 flex items-end justify-between border-b border-gray-800 pb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Add New Client</h1>
                        <p className="text-gray-500 text-sm mt-1 font-mono">Create a comprehensive client profile</p>
                    </div>
                    <button onClick={handleCancel} className="px-4 py-2 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 uppercase border border-gray-600">
                        ← Back to Clients
                    </button>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className="mb-6 p-4 rounded border bg-red-900/20 border-red-800 text-red-400">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-6">
                    {/* Personal Information */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('personal')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">👤 Personal Information</h3>
                            <span className="text-gray-400">{expandedSections.personal ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.personal && (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">First Name *</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.first_name ? 'border-red-500' : 'border-gray-600'} bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500`}
                                        placeholder="John"
                                    />
                                    {errors.first_name && <p className="text-xs text-red-400 mt-1">{errors.first_name}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Middle Name</label>
                                    <input
                                        type="text"
                                        name="middle_name"
                                        value={formData.middle_name}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="Michael"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Last Name *</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.last_name ? 'border-red-500' : 'border-gray-600'} bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500`}
                                        placeholder="Doe"
                                    />
                                    {errors.last_name && <p className="text-xs text-red-400 mt-1">{errors.last_name}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Date of Birth</label>
                                    <input
                                        type="date"
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Gender</label>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                        <option value="prefer_not_to_say">Prefer not to say</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Nationality</label>
                                    <input
                                        type="text"
                                        name="nationality"
                                        value={formData.nationality}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Indian, American"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Marital Status</label>
                                    <select
                                        name="marital_status"
                                        value={formData.marital_status}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Select status</option>
                                        <option value="single">Single</option>
                                        <option value="married">Married</option>
                                        <option value="divorced">Divorced</option>
                                        <option value="widowed">Widowed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Passport Number</label>
                                    <input
                                        type="text"
                                        name="passport_number"
                                        value={formData.passport_number}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="ABC123456"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('contact')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">📧 Contact Information</h3>
                            <span className="text-gray-400">{expandedSections.contact ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.contact && (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Primary Email *</label>
                                    <input
                                        type="email"
                                        name="primary_email"
                                        value={formData.primary_email}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.primary_email ? 'border-red-500' : 'border-gray-600'} bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500`}
                                        placeholder="john.doe@example.com"
                                    />
                                    {errors.primary_email && <p className="text-xs text-red-400 mt-1">{errors.primary_email}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Secondary Email</label>
                                    <input
                                        type="email"
                                        name="secondary_email"
                                        value={formData.secondary_email}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.secondary_email ? 'border-red-500' : 'border-gray-600'} bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500`}
                                        placeholder="john.personal@example.com"
                                    />
                                    {errors.secondary_email && <p className="text-xs text-red-400 mt-1">{errors.secondary_email}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Primary Phone *</label>
                                    <input
                                        type="tel"
                                        name="primary_phone"
                                        value={formData.primary_phone}
                                        onChange={handleChange}
                                        className={`w-full border ${errors.primary_phone ? 'border-red-500' : 'border-gray-600'} bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500`}
                                        placeholder="+1 234 567 8900"
                                    />
                                    {errors.primary_phone && <p className="text-xs text-red-400 mt-1">{errors.primary_phone}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Secondary Phone</label>
                                    <input
                                        type="tel"
                                        name="secondary_phone"
                                        value={formData.secondary_phone}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="+1 234 567 8901"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Address */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('address')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">🏠 Address (Optional)</h3>
                            <span className="text-gray-400">{expandedSections.address ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.address && (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Street Address</label>
                                    <input
                                        type="text"
                                        name="street_address"
                                        value={formData.street_address}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="123 Main Street, Apt 4B"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="New York"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">State/Province</label>
                                    <input
                                        type="text"
                                        name="state_province"
                                        value={formData.state_province}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="NY"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Postal Code</label>
                                    <input
                                        type="text"
                                        name="postal_code"
                                        value={formData.postal_code}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="10001"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Country</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="United States"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Education */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('education')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">🎓 Education (Optional)</h3>
                            <span className="text-gray-400">{expandedSections.education ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.education && (
                            <div className="p-6 space-y-4">
                                {education.map((edu, index) => (
                                    <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-xs font-bold text-gray-300 uppercase">Education #{index + 1}</h4>
                                            {education.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeEducation(index)}
                                                    className="text-red-400 hover:text-red-300 text-xs"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Education Level</label>
                                                <input
                                                    type="text"
                                                    value={edu.level}
                                                    onChange={(e) => handleEducationChange(index, 'level', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="Bachelor's, Master's, PhD"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Field of Study</label>
                                                <input
                                                    type="text"
                                                    value={edu.field_of_study}
                                                    onChange={(e) => handleEducationChange(index, 'field_of_study', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="Computer Science"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Institution</label>
                                                <input
                                                    type="text"
                                                    value={edu.institution}
                                                    onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="University Name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Graduation Year</label>
                                                <input
                                                    type="text"
                                                    value={edu.graduation_year}
                                                    onChange={(e) => handleEducationChange(index, 'graduation_year', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="2020"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addEducation}
                                    className="px-3 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase"
                                >
                                    + Add Education
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Work Experience */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('experience')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">💼 Work Experience (Optional)</h3>
                            <span className="text-gray-400">{expandedSections.experience ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.experience && (
                            <div className="p-6 space-y-4">
                                {experience.map((exp, index) => (
                                    <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-xs font-bold text-gray-300 uppercase">Experience #{index + 1}</h4>
                                            {experience.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeExperience(index)}
                                                    className="text-red-400 hover:text-red-300 text-xs"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Job Title</label>
                                                <input
                                                    type="text"
                                                    value={exp.job_title}
                                                    onChange={(e) => handleExperienceChange(index, 'job_title', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="Software Engineer"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Company</label>
                                                <input
                                                    type="text"
                                                    value={exp.company}
                                                    onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="Tech Corp"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Years of Experience</label>
                                                <input
                                                    type="text"
                                                    value={exp.years}
                                                    onChange={(e) => handleExperienceChange(index, 'years', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="3 years"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Industry</label>
                                                <input
                                                    type="text"
                                                    value={exp.industry}
                                                    onChange={(e) => handleExperienceChange(index, 'industry', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="Technology"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addExperience}
                                    className="px-3 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase"
                                >
                                    + Add Experience
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Languages */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('languages')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">🌐 Language Skills (Optional)</h3>
                            <span className="text-gray-400">{expandedSections.languages ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.languages && (
                            <div className="p-6 space-y-4">
                                {languages.map((lang, index) => (
                                    <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-xs font-bold text-gray-300 uppercase">Language #{index + 1}</h4>
                                            {languages.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeLanguage(index)}
                                                    className="text-red-400 hover:text-red-300 text-xs"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Language</label>
                                                <input
                                                    type="text"
                                                    value={lang.language}
                                                    onChange={(e) => handleLanguageChange(index, 'language', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="English, Spanish, etc."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Proficiency</label>
                                                <select
                                                    value={lang.proficiency}
                                                    onChange={(e) => handleLanguageChange(index, 'proficiency', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white text-sm"
                                                >
                                                    <option value="">Select level</option>
                                                    <option value="native">Native</option>
                                                    <option value="fluent">Fluent</option>
                                                    <option value="advanced">Advanced</option>
                                                    <option value="intermediate">Intermediate</option>
                                                    <option value="beginner">Beginner</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Test Type</label>
                                                <input
                                                    type="text"
                                                    value={lang.test_type}
                                                    onChange={(e) => handleLanguageChange(index, 'test_type', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="IELTS, TOEFL, etc."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Test Score</label>
                                                <input
                                                    type="text"
                                                    value={lang.test_score}
                                                    onChange={(e) => handleLanguageChange(index, 'test_score', e.target.value)}
                                                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 text-sm"
                                                    placeholder="7.5, 110, etc."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addLanguage}
                                    className="px-3 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase"
                                >
                                    + Add Language
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Financial Information */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('financial')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">💰 Financial Information (Optional)</h3>
                            <span className="text-gray-400">{expandedSections.financial ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.financial && (
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Annual Income</label>
                                    <input
                                        type="text"
                                        name="annual_income"
                                        value={financial.annual_income}
                                        onChange={handleFinancialChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="$50,000"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Source of Funds</label>
                                    <input
                                        type="text"
                                        name="source_of_funds"
                                        value={financial.source_of_funds}
                                        onChange={handleFinancialChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="Employment, Savings, etc."
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Financial Notes</label>
                                    <textarea
                                        name="financial_notes"
                                        value={financial.financial_notes}
                                        onChange={handleFinancialChange}
                                        rows={3}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="Additional financial information..."
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Preferences & Consent */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleSection('preferences')}
                            className="w-full bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between hover:bg-gray-900/70 transition-colors"
                        >
                            <h3 className="text-sm font-bold text-white uppercase tracking-wider">⚙️ Preferences & Consent</h3>
                            <span className="text-gray-400">{expandedSections.preferences ? '▼' : '▶'}</span>
                        </button>
                        {expandedSections.preferences && (
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Communication Preference</label>
                                    <select
                                        name="communication_preference"
                                        value={formData.communication_preference}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="marketing_consent"
                                        name="marketing_consent"
                                        checked={formData.marketing_consent}
                                        onChange={handleChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="marketing_consent" className="text-sm text-gray-300">Marketing Consent</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="data_processing_consent"
                                        name="data_processing_consent"
                                        checked={formData.data_processing_consent}
                                        onChange={handleChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="data_processing_consent" className="text-sm text-gray-300">Data Processing Consent</label>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Tags (comma-separated)</label>
                                    <input
                                        type="text"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleChange}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="vip, student, corporate"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Notes</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={4}
                                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                                        placeholder="Additional notes about the client..."
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-800">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-3 rounded text-sm font-bold bg-gray-700 text-white hover:bg-gray-600 uppercase border border-gray-600"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-3 rounded text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase border border-blue-500 shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Creating Client...' : 'Create Client'}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AddClientPage;
